import React, { FunctionComponent } from 'react';
import FormSuccessSubmit from '@/components/FormSuccessSubmit/FormSuccessSubmit';
import { Button, Container, InlineLoader } from '@visalex/core';
import { emailPattern, NullableSelectOption, SelectOption, useForm } from '@visalex/forms';
import { isValidPhoneNumber } from 'react-phone-number-input';

import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import styles from './ContactUsFormSection.module.scss';

export const prefix = 'contact-us';

export type FormFields = {
  firstName?: string;
  lastName?: string;
  email?: string;
  language?: NullableSelectOption;
  nationality?: NullableSelectOption;

  reason?: { label: string; value: string };
  phoneNumber?: string;
  comments?: string;
};

const formPrefix = 'common.forms';

type ContactUsFormSectionProps = {
  pageIsLoading: boolean;
  nationalities: SelectOption[];
  languages: SelectOption[];
  handleSubmit: (formData: FormFields) => Promise<void>;
  isSubmitWithError: boolean;
  isLoading: boolean;
};

export const ContactUsFormSection: FunctionComponent<ContactUsFormSectionProps> = ({
  pageIsLoading,
  nationalities,
  languages,
  handleSubmit,
  isSubmitWithError,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();

  const { Form, formState } = useForm<FormFields>({ onSubmit: handleSubmit });

  return (
    <Container className={styles[`${prefix}__container`]}>
      <Form key={i18n.language}>
        {!formState.isSubmitSuccessful || isSubmitWithError ? (
          <div className={styles[`${prefix}-form`]}>
            <div className={styles[`${prefix}-form__header`]}>
              <Image src="/icons/icon-question.svg" alt={t(`${prefix}.form.title`)} width={100} height={100} />
              <h3>{t(`${prefix}.form.title`)}</h3>
              <p>{t(`${prefix}.form.subtitle`)}</p>
            </div>

            <div className={!pageIsLoading ? styles[`${prefix}-form__fade-in`] : ''}>
              {pageIsLoading ? (
                <InlineLoader />
              ) : (
                <div data-testid="contact-us-form">
                  <div className={styles[`${prefix}-form__body`]}>
                    <Form.Input
                      name="first-name"
                      rules={{
                        required: t(`${formPrefix}.first-name.rules.required`),
                      }}
                      placeholder={t(`${formPrefix}.first-name.placeholder`)}
                    />
                    <Form.Input
                      name="last-name"
                      rules={{
                        required: t(`${formPrefix}.last-name.rules.required`),
                      }}
                      placeholder={t(`${formPrefix}.last-name.placeholder`)}
                    />
                    <Form.Input
                      name="email"
                      rules={{
                        required: t(`${formPrefix}.email.rules.required`),
                        pattern: {
                          value: emailPattern,
                          message: t(`${formPrefix}.email.rules.format`),
                        },
                      }}
                      placeholder={t(`${formPrefix}.email.placeholder`)}
                    />
                    <Form.Select
                      name="language"
                      placeholder={t(`${formPrefix}.language.placeholder`)}
                      rules={{
                        required: t(`${formPrefix}.language.rules.required`),
                      }}
                      options={languages}
                    />
                    <Form.Select
                      name="nationality"
                      placeholder={t(`${formPrefix}.nationality.placeholder`)}
                      rules={{
                        required: t(`${formPrefix}.nationality.rules.required`),
                      }}
                      options={nationalities}
                    />
                    <Form.PhoneInput
                      name="phone"
                      rules={{
                        required: t(`${formPrefix}.phone.rules.required`),
                        validate: (value) => isValidPhoneNumber(value) || t(`${formPrefix}.phone.rules.format`),
                      }}
                      placeholder={t(`${formPrefix}.phone.placeholder`)}
                    />
                    <Form.TextArea
                      name="comments"
                      className={styles[`${prefix}-form__body__textarea`]}
                      placeholder={t(`${formPrefix}.comments.placeholder`)}
                    />
                  </div>
                  <div className={styles[`${prefix}-form__body__controls`]}>
                    <Button
                      className={styles[`${prefix}-form__body__controls_button`]}
                      type="submit"
                      loading={isLoading}
                    >
                      {t(`${prefix}.form.button-text`)}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <FormSuccessSubmit description={t('form-success-submit.description')} />
        )}
      </Form>
    </Container>
  );
};
